"use no memo";

import { TableCombinationSchema } from "@repo/validators";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type { TableCombination } from "~/providers/table-provider";
import { useTable } from "~/hooks/use-table";
import { useTableManager } from "~/managers/use-table-manager";
import { useUnsavedChanges } from "~/providers/unsaved-changes-provider";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useForm,
} from "../ui/form";
import { ModalInput } from "../ui/modal-input";
import { MultiSelect } from "../ui/multi-select";

export default function TableCombinationForm({
  tableCombination,
  onClose,
}: {
  tableCombination?: TableCombination;
  onClose: (force?: boolean) => void;
}) {
  const { t } = useTranslation("common");

  const { tables } = useTable();

  const { createTableCombination, updateTableCombination } = useTableManager();

  const { setUnsavedChanges } = useUnsavedChanges();

  const form = useForm({
    reValidateMode: "onChange",
    schema: TableCombinationSchema(),
    defaultValues: {
      groupSize: tableCombination?.groupSize ?? 1,
      tables: tableCombination?.tables.map((table) => table.id) ?? [],
      seats: tableCombination?.tables.reduce(
        (acc, curr) => acc + curr.maxSeats,
        0,
      ),
    },
  });

  const seats = useWatch({
    control: form.control,
    name: "seats",
  });

  const isDirty = !!Object.values(form.formState.dirtyFields).length;

  const tableOptions = tables.map((table) => ({
    label: `${table.reference} (${table.maxSeats} seats)`,
    value: table.id,
  }));

  setUnsavedChanges({
    isDirty,
    onDiscard() {
      form.reset();
      onClose(true);
    },
  });

  const handleSubmit = form.handleSubmit(async (formData) => {
    if (tableCombination) {
      const { data } = await updateTableCombination({
        ...formData,
        id: tableCombination.id,
      });
      if (data === true) {
        form.reset();
        onClose(true);
      }
    } else {
      const { data, error } = await createTableCombination(formData);
      if (data) {
        form.reset();
        onClose(true);
      } else if (error) {
        form.setError(error.path, { message: error.message });
      }
    }
  });

  return (
    <Form {...form}>
      <form
        id="table-combination-form"
        onSubmit={handleSubmit}
        className="grid grid-cols-2 gap-2"
      >
        <FormField
          control={form.control}
          name="groupSize"
          render={({ field }) => (
            <FormItem className="col-span-full ">
              <FormLabel>{t("TableCombinations.group_size")}</FormLabel>
              <FormControl>
                <ModalInput
                  min={0}
                  max={25}
                  label={t("TableCombinations.group_size")}
                  type="number"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="tables"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("TableCombinations.tables")}</FormLabel>
              <MultiSelect
                placeholder="Select tables..."
                onValueChange={(value) => {
                  field.onChange(value);
                  const selectedTables = tables.filter((table) =>
                    value.includes(table.id),
                  );
                  const selectedSeats = selectedTables.reduce(
                    (acc, curr) => acc + curr.maxSeats,
                    0,
                  );
                  form.setValue("seats", selectedSeats);
                }}
                options={tableOptions}
                defaultValue={field.value}
                value={field.value}
              />
              <span className="text-xs mt-1">
                {t("TableCombinations.seats_x", {
                  x: seats,
                })}
              </span>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
}
